import { useEffect, useState } from "react";
import GradientText from "../components/GradientText";
import Seo from "../components/Seo";

const neonColors = ["#ff43b8", "#2ed4ff", "#ff8a22", "#39d353", "#8b49ff"];

interface MenuItem {
  name: string;
  description: string;
  price: number;
  image?: string;
  tags: string[];
}

interface MenuModifierOption {
  name: string;
  price?: number;
}

interface MenuModifierGroup {
  title: string;
  options: MenuModifierOption[];
}

interface MenuCategory {
  name: string;
  id: string;
  items: MenuItem[];
  modifiers?: MenuModifierGroup[];
  modifiersToggleLabel?: string;
}

interface MenuData {
  categories: MenuCategory[];
}

const tagLabels: Record<string, string> = {
  popular: "Popular",
  signature: "Signature",
  vegetarian: "Vegetarian",
  spicy: "Spicy",
  new: "New",
};

export default function MenuPage() {
  const [menuData, setMenuData] = useState<MenuData | null>(null);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openModifiers, setOpenModifiers] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    async function loadMenu() {
      try {
        const response = await fetch("/menu.json");
        if (!response.ok) throw new Error("Failed to load menu");
        const data = await response.json();
        setMenuData(data);
        setLoading(false);
      } catch (err) {
        console.error("Error loading menu:", err);
        setError(true);
        setLoading(false);
      }
    }
    loadMenu();
  }, []);

  const handleFilterClick = (category: string) => {
    setCurrentFilter(category);
  };

  const toggleModifiers = (categoryId: string) => {
    setOpenModifiers((current) => ({
      ...current,
      [categoryId]: !current[categoryId],
    }));
  };

  const filteredCategories =
    menuData?.categories.filter(
      (cat) => currentFilter === "all" || cat.id === currentFilter,
    ) || [];

  return (
    <>
      <Seo
        title="Menu"
        description="Browse the Retro Grill menu featuring crab cakes, smash burgers, hot chicken, wings, wraps, and house favorites."
        path="/menu"
        image="/images/MDCrabCake.webp"
      />
      {/* Page Header */}
      <section className="relative pt-[calc(80px+60px)] pb-[60px] px-6 text-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 page-header-gradient-bg"></div>
          <div className="absolute inset-0 hero-grid-bg"></div>
        </div>
        <div className="container">
          <GradientText
            colors={neonColors}
            animationSpeed={6}
            className="text-[clamp(2.5rem,8vw,4.5rem)] mb-4 font-display font-semibold"
          >
            Our Menu
          </GradientText>
          <p className="text-xl text-text-secondary font-light">
            Comfort food classics with a retro spin
          </p>
        </div>
      </section>

      {/* Menu Section */}
      <section className="py-[60px] pb-[100px] bg-bg-secondary min-h-[60vh]">
        <div className="container">
          {/* Filters */}
          <div
            className="flex justify-center gap-3 mb-[60px] flex-wrap"
            id="menu-filters"
          >
            {["all", "sandwiches", "platters", "sides", "drinks"].map((cat) => (
              <button
                key={cat}
                className={`filter-btn ${
                  currentFilter === cat ? "active" : ""
                }`}
                onClick={() => handleFilterClick(cat)}
              >
                {cat === "all"
                  ? "All Items"
                  : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>

          {/* Menu Grid */}
          <div
            className="flex flex-col gap-5 max-w-[900px] mx-auto"
            id="menu-grid"
            key={currentFilter}
          >
            {loading && (
              <div className="text-center py-20 text-text-secondary">
                <div className="loading-spinner"></div>
                <p>Loading delicious items...</p>
              </div>
            )}

            {error && (
              <div className="text-center py-20">
                <span className="text-[4rem] block mb-5">😅</span>
                <h3 className="font-body font-semibold text-text-primary mb-2">
                  Oops! Couldn't load the menu
                </h3>
                <p className="text-text-secondary">
                  Please try refreshing the page
                </p>
              </div>
            )}

            {!loading &&
              !error &&
              filteredCategories.map((category) => (
                <div key={`${currentFilter}-${category.id}`}>
                  {/* Category Header */}
                  <div className="py-[30px] pb-[15px]">
                    <h2 className="text-[2rem] text-text-primary relative inline-block font-display font-semibold">
                      {category.name}
                      <span className="absolute -bottom-2 left-0 w-[60px] h-[3px] rounded bg-gradient-to-r from-accent-blue via-accent-pink to-accent-purple"></span>
                    </h2>
                  </div>

                  {/* Menu Items */}
                  {category.items.map((item) => (
                    <MenuItemCard
                      key={`${currentFilter}-${category.id}-${item.name}`}
                      item={item}
                    />
                  ))}

                  {category.modifiers && category.modifiers.length > 0 && (
                    <div className="mt-5">
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-full border border-border-default bg-bg-card px-4 py-2 text-sm font-semibold text-text-primary transition-all duration-300 hover:border-accent-pink hover:text-accent-pink"
                        onClick={() => toggleModifiers(category.id)}
                        aria-expanded={Boolean(openModifiers[category.id])}
                      >
                        <span>{category.modifiersToggleLabel || "Modifiers"}</span>
                        <span
                          className={`transition-transform duration-300 ${
                            openModifiers[category.id] ? "rotate-180" : ""
                          }`}
                        >
                          ▼
                        </span>
                      </button>

                      {openModifiers[category.id] && (
                        <div className="mt-4 rounded-2xl border border-border-default/80 bg-bg-card/70 p-5 shadow-sm-custom">
                          <div className="space-y-5">
                            {category.modifiers.map((modifierGroup) => (
                              <div key={`${category.id}-${modifierGroup.title}`}>
                                <h3 className="mb-3 font-display text-base font-semibold uppercase tracking-[0.12em] text-accent-pink">
                                  {modifierGroup.title}
                                </h3>
                                <div className="divide-y divide-border-default/70 rounded-xl bg-bg-secondary/70">
                                  {modifierGroup.options.map((option) => (
                                    <div
                                      key={`${modifierGroup.title}-${option.name}`}
                                      className="flex items-center justify-between gap-4 px-4 py-3 text-[0.95rem]"
                                    >
                                      <span className="text-text-secondary">
                                        {option.name}
                                      </span>
                                      {typeof option.price === "number" && (
                                        <span className="font-semibold text-text-primary">
                                          {option.price === 0
                                            ? "Free"
                                            : `+$${option.price.toFixed(2)}`}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </section>
    </>
  );
}

function MenuItemCard({ item }: { item: MenuItem }) {
  const hasImage = item.image && item.image.trim() !== "";
  const tags = item.tags || [];

  return (
    <div className="flex flex-col md:flex-row bg-bg-card border border-border-default rounded-lg overflow-hidden transition-all duration-300 hover:border-accent-purple hover:shadow-md-custom hover:shadow-glow-pink animate-slide-up">
      {hasImage && (
        <div className="w-full md:w-[180px] md:min-w-[180px] h-[160px] md:h-[140px] overflow-hidden relative bg-bg-elevated">
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-400 hover:scale-105"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.parentElement!.innerHTML =
                '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-bg-elevated to-bg-tertiary"><span class="text-[2.5rem] opacity-50">🍽️</span></div>';
            }}
          />
        </div>
      )}
      <div className="p-4 md:px-5 flex-1 flex flex-col justify-center">
        <div className="flex justify-between items-start gap-4 mb-2">
          <h3 className="font-body font-semibold text-lg text-text-primary">
            {item.name}
          </h3>
          <span className="font-bold text-base text-accent-pink whitespace-nowrap">
            ${item.price.toFixed(2)}
          </span>
        </div>
        <p className="text-text-secondary text-[0.9rem] leading-relaxed mb-[10px]">
          {item.description}
        </p>
        {tags.length > 0 && (
          <div className="flex gap-[6px] flex-wrap">
            {tags.map((tag) => (
              <span key={tag} className={`tag tag-${tag}`}>
                {tagLabels[tag] || tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
